import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChatError,
  ChatMessage,
  ChatMeta,
  ChatPersona,
  RetrievedSource,
  fetchMeta,
  isConfigured,
  isMockMode,
  streamChat,
  warmup,
} from './cvChatClient';

/** La chat e' operativa con un backend reale oppure con il transport simulato. */
const isOperational = (): boolean => isConfigured() || isMockMode();

export type ChatStatus = 'idle' | 'connecting' | 'streaming' | 'error';

/** Soglie della UX di cold start, in millisecondi. */
const COLD_START_HINT_MS = 2500;
const COLD_START_LONG_MS = 8000;
const BACKEND_UNREACHABLE_FALLBACKS = [
  'Oggi non riesco a rispondere: ho finito i token e ho un budget limitato.',
  'Al momento non posso rispondere: budget terminato per oggi.',
  'Backend non raggiungibile: per oggi ho esaurito i token disponibili.',
];

export const getRandomBackendUnavailableMessage = (fallbacks?: string[]): string => {
  const messages = fallbacks?.filter((message) => message.trim().length > 0) ?? [];
  const source = messages.length > 0 ? messages : BACKEND_UNREACHABLE_FALLBACKS;
  return source[Math.floor(Math.random() * source.length)];
};

export interface UiMessage extends ChatMessage {
  id: string;
  sources?: RetrievedSource[];
  isError?: boolean;
}

let messageCounter = 0;
const nextId = (): string => {
  messageCounter += 1;
  return `m${messageCounter}`;
};

interface UseCvChatOptions {
  /** true quando la chat e' visibile: attiva il warmup del backend. */
  active: boolean;
  persona: ChatPersona;
  backendUnavailableFallbacks?: string[];
}

export function useCvChat({ active, persona, backendUnavailableFallbacks }: UseCvChatOptions) {
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [status, setStatus] = useState<ChatStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<ChatMeta | null>(null);
  /** 0 = nessuna attesa anomala, 1 = risveglio in corso, 2 = attesa lunga */
  const [coldStartLevel, setColdStartLevel] = useState<0 | 1 | 2>(0);

  /**
   * Cooldown fra due domande. Il backend consente una chiamata al minuto: il
   * countdown lo rende visibile invece di far scoprire il limite con un 429,
   * che con una finestra cosi' stretta sarebbe l'esito piu' frequente.
   */
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [clockTick, setClockTick] = useState(() => Date.now());

  const abortRef = useRef<AbortController | null>(null);
  const timersRef = useRef<number[]>([]);
  const warmedRef = useRef(false);
  /** Ampiezza della finestra dichiarata dal backend, in millisecondi. */
  const windowMsRef = useRef(60_000);
  // La persona corrente serve dentro send() senza ricrearne l'identita' a ogni
  // cambio di tema, che azzererebbe i callback dei componenti figli.
  const personaRef = useRef(persona);
  personaRef.current = persona;

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
    setColdStartLevel(0);
  }, []);

  // Alla prima apertura: sveglia il container e carica i metadati. Sposta il
  // cold start fuori dal percorso critico, prima che l'utente scriva.
  useEffect(() => {
    if (!active || warmedRef.current || !isOperational()) return;
    warmedRef.current = true;

    const controller = new AbortController();
    void warmup(controller.signal);
    void fetchMeta(controller.signal).then((value) => {
      if (!value) return;
      setMeta(value);
      if (value.rate_limit_window_seconds > 0) {
        windowMsRef.current = value.rate_limit_window_seconds * 1000;
      }
    });

    return () => controller.abort();
  }, [active]);

  // Tick del countdown: attivo solo mentre il cooldown e' in corso.
  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const id = window.setInterval(() => {
      const now = Date.now();
      setClockTick(now);
      if (now >= cooldownUntil) window.clearInterval(id);
    }, 500);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  const cooldownSeconds = Math.max(0, Math.ceil((cooldownUntil - clockTick) / 1000));

  useEffect(
    () => () => {
      abortRef.current?.abort();
      timersRef.current.forEach((id) => window.clearTimeout(id));
    },
    []
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    clearTimers();
    setStatus('idle');
  }, [clearTimers]);

  const reset = useCallback(() => {
    stop();
    setMessages([]);
    setError(null);
    // Il cooldown NON viene azzerato: svuotare la conversazione non deve
    // diventare il modo per aggirare il rate limit.
  }, [stop]);

  const send = useCallback(
    async (rawText: string) => {
      const text = rawText.trim();
      if (!text || status === 'connecting' || status === 'streaming') return;
      if (Date.now() < cooldownUntil) return;

      setError(null);
      setStatus('connecting');

      // Il messaggio di risposta viene creato subito vuoto: i token in arrivo lo
      // riempiono in place, senza rimontare la lista a ogni delta.
      const userMessage: UiMessage = { id: nextId(), role: 'user', content: text };
      const assistantId = nextId();

      const history: ChatMessage[] = [
        ...messages.filter((m) => !m.isError).map(({ role, content }) => ({ role, content })),
        { role: 'user', content: text },
      ];

      setMessages((prev) => [
        ...prev,
        userMessage,
        { id: assistantId, role: 'assistant', content: '' },
      ]);

      const controller = new AbortController();
      abortRef.current = controller;

      timersRef.current.push(
        window.setTimeout(() => setColdStartLevel(1), COLD_START_HINT_MS),
        window.setTimeout(() => setColdStartLevel(2), COLD_START_LONG_MS)
      );

      const appendToAssistant = (chunk: string) => {
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + chunk } : m))
        );
      };

      try {
        await streamChat({
          messages: history,
          persona: personaRef.current,
          signal: controller.signal,
          onEvent: (event) => {
            switch (event.type) {
              case 'token':
                // Il primo token conferma che il backend e' sveglio.
                clearTimers();
                setStatus('streaming');
                appendToAssistant(event.text);
                break;

              case 'sources':
                setMessages((prev) =>
                  prev.map((m) => (m.id === assistantId ? { ...m, sources: event.items } : m))
                );
                break;

              case 'budget_exhausted':
                clearTimers();
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId ? { ...m, content: event.message, isError: true } : m
                  )
                );
                break;

              case 'error':
                clearTimers();
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId ? { ...m, content: event.message, isError: true } : m
                  )
                );
                setError(event.message);
                break;

              case 'done':
                if (event.budget) {
                  const snapshot = event.budget;
                  setMeta((prev) => (prev ? { ...prev, budget: snapshot } : prev));
                }
                break;

              default:
                break;
            }
          },
        });

        setStatus('idle');
        // Cooldown preventivo: la prossima domanda sarebbe respinta comunque.
        setCooldownUntil(Date.now() + windowMsRef.current);
      } catch (err) {
        const chatError = err instanceof ChatError ? err : null;

        if (chatError?.code === 'rate_limited') {
          const wait = (chatError.retryAfterSeconds ?? 60) * 1000;
          setCooldownUntil(Date.now() + wait);
        }

        if (chatError?.code === 'aborted') {
          // Interruzione volontaria: rimuove la bolla vuota senza segnalare errore.
          setMessages((prev) => prev.filter((m) => !(m.id === assistantId && m.content === '')));
          setStatus('idle');
          return;
        }

        const message =
          chatError?.code === 'network'
            ? getRandomBackendUnavailableMessage(backendUnavailableFallbacks)
            : chatError?.message ?? 'Qualcosa non ha funzionato. Riprova tra poco.';

        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, content: message, isError: true } : m))
        );
        setError(message);
        setStatus('error');
      } finally {
        clearTimers();
        abortRef.current = null;
      }
    },
    [messages, status, clearTimers, cooldownUntil, backendUnavailableFallbacks]
  );

  const isBusy = status === 'connecting' || status === 'streaming';

  return {
    messages,
    status,
    error,
    meta,
    coldStartLevel,
    isBusy,
    cooldownSeconds,
    canSend: !isBusy && cooldownSeconds === 0,
    send,
    stop,
    reset,
    configured: isOperational(),
    /** true quando le risposte arrivano dal transport simulato, non dal backend. */
    simulated: isMockMode(),
  };
}
